import { Search, X } from '@/components/ui/icons';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import type { SearchResultDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { searchApi, type SearchParams } from '@/api/endpoints';
import { MessageContent } from '@/components/chat/MessageContent';
import { Avatar } from '@/components/ui/Avatar';
import { Button, IconButton } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/Feedback';
import { Spinner } from '@/components/ui/Spinner';
import { formatMessageTime } from '@/lib/time';
import { useAuth } from '@/stores/auth';
import { targetKey } from '@/stores/messages';
import { useServerChannels, useServerMembers } from '@/stores/servers';
import { useConversations } from '@/stores/social';
import { useUi } from '@/stores/ui';
import { useUser } from '@/stores/users';

interface Scope {
  serverId?: string;
  channelId?: string;
  conversationId?: string;
}

type Where = 'channel' | 'server' | 'everywhere';

function dayStart(date: string) {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

export function SearchPanel({ scope, onClose }: { scope: Scope; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [where, setWhere] = useState<Where>(scope.channelId || scope.conversationId ? 'channel' : 'server');
  const [channelFilter, setChannelFilter] = useState('');
  const [authorId, setAuthorId] = useState('');
  const [after, setAfter] = useState('');
  const [before, setBefore] = useState('');
  const [has, setHas] = useState<'' | 'attachment' | 'image' | 'link'>('');
  const [result, setResult] = useState<SearchResultDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<SearchParams | null>(null);
  const abort = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const channels = useServerChannels(scope.serverId).filter((c) => c.type === 'TEXT');
  const members = useServerMembers(scope.serverId);
  const me = useAuth((s) => s.user);
  const recipients = useConversations((s) => (scope.conversationId ? s.conversations[scope.conversationId]?.recipients : undefined));
  const authors = useMemo(
    () => (scope.serverId ? members.map((m) => m.user) : [...(recipients ?? []), me].filter((u) => !!u)).sort((a, b) => a!.displayName.localeCompare(b!.displayName)),
    [scope.serverId, members, recipients, me],
  );

  useEffect(() => inputRef.current?.focus(), []);
  useEffect(() => () => abort.current?.abort(), []);

  const run = async (p: SearchParams, append = false) => {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await searchApi.search(p, controller.signal);
      setResult((prev) => (append && prev ? { total: data.total, results: [...prev.results, ...data.results] } : data));
      setParams(p);
    } catch (err) {
      if ((err as Error).name !== 'AbortError') setError(errorMessage(err));
    } finally {
      if (abort.current === controller) setLoading(false);
    }
  };

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const p: SearchParams = { q: q.trim() || undefined, authorId: authorId || undefined, has: has || undefined, offset: 0 };
    if (scope.conversationId) p.conversationId = scope.conversationId;
    else if (where === 'channel' && scope.channelId) p.channelId = scope.channelId;
    else if (where === 'server' && scope.serverId) {
      if (channelFilter) p.channelId = channelFilter;
      else p.serverId = scope.serverId;
    }
    if (after) p.after = dayStart(after).toISOString();
    if (before) {
      const end = dayStart(before);
      end.setDate(end.getDate() + 1); // inclusive of the chosen day
      p.before = end.toISOString();
    }
    if (!p.q && !p.authorId && !p.has && !p.after && !p.before && !p.channelId) {
      setError('Type something to search for, or pick a filter.');
      return;
    }
    void run(p);
  };

  const field = 'h-9 w-full rounded-lg border border-line bg-sunken px-2 text-sm text-fg focus:border-accent focus:outline-none';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-[52px] shrink-0 items-center justify-between border-b border-line px-4">
        <h2 className="text-sm font-bold">Search messages</h2>
        <IconButton label="Close search" size="sm" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-2 border-b border-line p-3">
        <label className="relative block">
          <Search size={15} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-muted" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" aria-label="Search text" maxLength={200} className={`${field} pl-8`} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          {!scope.conversationId && (
            <label className="col-span-2 text-xs font-semibold text-fg-muted">
              Where
              <select value={where} onChange={(e) => setWhere(e.target.value as Where)} className={`${field} mt-1`}>
                {scope.channelId && <option value="channel">This channel</option>}
                {scope.serverId && <option value="server">This server</option>}
                <option value="everywhere">All servers & DMs</option>
              </select>
            </label>
          )}
          {where === 'server' && scope.serverId && (
            <label className="col-span-2 text-xs font-semibold text-fg-muted">
              In channel
              <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} className={`${field} mt-1`}>
                <option value="">Any channel</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    #{c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="col-span-2 text-xs font-semibold text-fg-muted">
            From
            <select value={authorId} onChange={(e) => setAuthorId(e.target.value)} className={`${field} mt-1`}>
              <option value="">Anyone</option>
              {authors.map((u) => (
                <option key={u!.id} value={u!.id}>
                  {u!.displayName} (@{u!.username})
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-semibold text-fg-muted">
            After
            <input type="date" value={after} onChange={(e) => setAfter(e.target.value)} className={`${field} mt-1`} />
          </label>
          <label className="text-xs font-semibold text-fg-muted">
            Before
            <input type="date" value={before} onChange={(e) => setBefore(e.target.value)} className={`${field} mt-1`} />
          </label>
          <label className="col-span-2 text-xs font-semibold text-fg-muted">
            Has
            <select value={has} onChange={(e) => setHas(e.target.value as typeof has)} className={`${field} mt-1`}>
              <option value="">Anything</option>
              <option value="attachment">A file</option>
              <option value="image">An image</option>
              <option value="link">A link</option>
            </select>
          </label>
        </div>
        <Button type="submit" size="sm" loading={loading && !result}>
          Search
        </Button>
      </form>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {error && <p className="p-3 text-sm text-danger" role="alert">{error}</p>}
        {!result && !error && !loading && <p className="p-4 text-center text-sm text-fg-muted">Search runs on the server across everything you can access.</p>}
        {result && (
          <>
            <p className="px-2 pb-2 text-xs font-semibold text-fg-muted">
              {result.total === 0 ? 'No results' : `${result.total} ${result.total === 1 ? 'result' : 'results'}`}
            </p>
            {result.total === 0 && <EmptyState art="search" title="Nothing found" body="Try different words or fewer filters." />}
            <ul className="flex flex-col gap-2">
              {result.results.map((r) => (
                <SearchResult key={r.message.id} item={r} onNavigate={onClose} />
              ))}
            </ul>
            {result.results.length < result.total && params && (
              <Button variant="secondary" size="sm" block className="mt-3" loading={loading} onClick={() => void run({ ...params, offset: result.results.length }, true)}>
                Load more
              </Button>
            )}
          </>
        )}
        {loading && !result && (
          <div className="flex justify-center py-6 text-fg-muted">
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
}

function SearchResult({ item, onNavigate }: { item: SearchResultDTO['results'][number]; onNavigate: () => void }) {
  const { message } = item;
  const author = useUser(message.author?.id, message.author);
  const navigate = useNavigate();
  const requestJump = useUi((s) => s.requestJump);
  const isWide = window.matchMedia('(min-width: 1200px)').matches;

  const jump = () => {
    const path = message.conversationId ? `/app/@me/${message.conversationId}` : `/app/${message.serverId}/${message.channelId}`;
    navigate(path);
    requestJump(targetKey(message), message.id);
    if (!isWide) onNavigate();
  };

  return (
    <li>
      <button type="button" onClick={jump} className="w-full rounded-xl border border-line bg-surface p-3 text-left transition-colors hover:border-line-strong hover:bg-hover/40">
        <p className="mb-1.5 truncate text-[11px] font-bold tracking-wide text-fg-muted uppercase">
          {item.channelName ? `#${item.channelName}` : 'Direct message'}
          {item.serverName && ` · ${item.serverName}`}
        </p>
        <div className="flex gap-2.5">
          <Avatar user={author} size={28} />
          <div className="min-w-0 flex-1">
            <p className="flex items-baseline gap-2">
              <span className="truncate text-sm font-semibold">{author?.displayName ?? 'Deleted User'}</span>
              <span className="shrink-0 text-[11px] text-fg-muted">{formatMessageTime(message.createdAt)}</span>
            </p>
            <MessageContent content={message.content.length > 400 ? `${message.content.slice(0, 400)}…` : message.content} serverId={message.serverId} interactive={false} className="text-sm text-fg-soft" />
            {message.attachments.length > 0 && <p className="mt-1 text-xs text-fg-muted">📎 {message.attachments.length} attachment(s)</p>}
          </div>
        </div>
      </button>
    </li>
  );
}
